import { useMemo } from 'react';

import styled from 'styled-components';
import type { ColumnDef } from '@tanstack/react-table';

import { DataTable } from 'components/DataTable';
import { EllipsisTooltip } from 'components/EllipsisTooltip';

import { formatDateForDisplay } from 'helpers/dateTimes';
import { renderAsPrice } from 'helpers/price';
import { DELIVERY_DAYS } from 'pages/Products/constants/weekdays';

import { StatusTag } from './components/StatusTag';
import type { OrderRow } from './constants/orderBuckets';

type OrdersDataTableViewProps = {
    orders: OrderRow[];
    selectedOrderIds: string[];
    setSelectedOrderIds: React.Dispatch<React.SetStateAction<string[]>>;
    /** La pagination appartient à la page, partagée avec le kanban : la table la consomme au défilement. */
    hasMore: boolean;
    onLoadMore: () => Promise<unknown>;
};

export const OrdersDataTableView = ({
    orders,
    selectedOrderIds,
    setSelectedOrderIds,
    hasMore,
    onLoadMore,
}: OrdersDataTableViewProps) => {
    const columns = useMemo<ColumnDef<OrderRow, unknown>[]>(
        () => [
            {
                id: 'organization',
                accessorFn: (row) => row.organization.name,
                header: 'Organisation',
                cell: ({ row }) => (
                    <EllipsisTooltip
                        text={row.original.organization.name}
                        className="max-w-[220px] font-semibold text-foreground"
                    />
                ),
            },
            {
                id: 'number',
                accessorFn: (row) => row.number ?? 0,
                header: 'N°',
                cell: ({ row }) => (row.original.number != null ? `N°${row.original.number}` : '-'),
            },
            {
                id: 'totem',
                accessorFn: (row) => row.totem.name ?? row.totem.number,
                header: 'Totem',
                cell: ({ row }) => {
                    const { name, number } = row.original.totem;
                    return name ? `${name} (N°${number})` : `N°${number}`;
                },
            },
            {
                accessorKey: 'state',
                header: 'État',
                cell: ({ row }) => <StatusTag state={row.original.state} />,
            },
            {
                id: 'date_delivery',
                accessorFn: (row) => row.date_delivery ?? '',
                header: 'Date de livraison',
                cell: ({ row }) =>
                    row.original.date_delivery ? formatDateForDisplay(row.original.date_delivery) : '-',
            },
            {
                id: 'weekday',
                accessorFn: (row) => DELIVERY_DAYS.find(({ name }) => name === row.weekday)?.label ?? row.weekday ?? '',
                header: 'Jour',
            },
            {
                id: 'isPunctual',
                accessorFn: (row) => (row.isPunctual ? 'Ponctuelle' : 'Récurrente'),
                header: 'Type',
            },
            {
                id: 'first_order',
                accessorFn: (row) => (row.first_order ? 'Oui' : 'Non'),
                header: 'Première',
            },
            {
                id: 'priceHT',
                accessorFn: (row) => row.prices.priceHT ?? 0,
                header: 'Prix HT',
                cell: ({ getValue }) => renderAsPrice(getValue<number>()),
            },
            {
                id: 'priceTTC',
                accessorFn: (row) => row.prices.priceTTC ?? 0,
                header: 'Prix TTC',
                cell: ({ getValue }) => renderAsPrice(getValue<number>()),
            },
        ],
        [],
    );

    return (
        <TableContainer>
            <DataTable<OrderRow>
                columns={columns}
                data={orders}
                filterMode="client"
                getRowId={(row) => row._id}
                getRowLink={(row) => `/order/${row._id}`}
                defaultSortBy="organization"
                defaultSortOrder="asc"
                emptyMessage="Aucune commande."
                enableSelection
                selectedKeys={selectedOrderIds}
                onSelectionChange={setSelectedOrderIds}
                hasMore={hasMore}
                fetchMore={onLoadMore}
                enableVirtualization
            />
        </TableContainer>
    );
};

const TableContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    overflow: hidden;
`;
